import React, { useState, useMemo } from 'react';
import { MdSearch, MdPerson, MdSchool } from 'react-icons/md';

/**
 * Student List Component
 * Displays list of students with attendance checkboxes
 */
function StudentList({ 
  students, 
  attendance, 
  onAttendanceChange, 
  readOnly = false,
  showSearch = true 
}) {
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * Extract student name from student_name field (remove batch in parentheses)
   * Example: "Ahmed Ali (CS0119)" → "Ahmed Ali"
   */
  const extractStudentName = (studentName) => {
    if (!studentName) return 'Unknown Student';
    // Remove batch in parentheses: "Name (Batch)" → "Name"
    return studentName.replace(/\s*\([^)]*\)\s*$/, '').trim() || 'Unknown Student';
  };

  // Sort and filter students
  const filteredStudents = useMemo(() => {
    // First, sort students by student_no in ascending order
    const sortedStudents = [...students].sort((a, b) => {
      const idA = (a.student_no || '').toString();
      const idB = (b.student_no || '').toString();
      // Natural sort for numeric IDs
      const numA = parseInt(idA) || 0;
      const numB = parseInt(idB) || 0;
      if (numA !== numB) return numA - numB;
      // If numeric comparison fails, use string comparison
      return idA.localeCompare(idB);
    });

    // Then filter by search term if provided
    if (!searchTerm) return sortedStudents;
    
    const term = searchTerm.toLowerCase();
    return sortedStudents.filter(student => {
      const studentName = extractStudentName(student.student_name || '').toLowerCase();
      const studentNo = (student.student_no || '').toLowerCase();
      const batch = (student.batch || '').toLowerCase();
      
      return studentName.includes(term) || studentNo.includes(term) || batch.includes(term);
    });
  }, [students, searchTerm]);

  const handleToggle = (studentId) => {
    if (readOnly) return;
    
    const currentStatus = attendance[studentId];
    const newStatus = currentStatus === 'present' ? 'absent' : 'present';
    onAttendanceChange(studentId, newStatus);
  };

  const handleSelectAll = (status) => {
    if (readOnly) return;
    
    filteredStudents.forEach(student => {
      onAttendanceChange(student.id || student.student_id, status);
    });
  };

  const presentCount = Object.values(attendance).filter(status => status === 'present').length;
  const absentCount = Object.values(attendance).filter(status => status === 'absent').length;
  const totalCount = students.length;
  const showBatchGroups = new Set(students.map(student => student.batch).filter(Boolean)).size > 1;
  const studentGroups = useMemo(() => {
    const groups = new Map();
    filteredStudents.forEach(student => {
      const batch = student.batch || 'Other students';
      if (!groups.has(batch)) groups.set(batch, []);
      groups.get(batch).push(student);
    });
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filteredStudents]);

  const renderStudentCard = (student) => {
    const studentId = student.student_id || student.id;
    const status = attendance[studentId] || null;
    const isPresent = status === 'present';
    const isAbsent = status === 'absent';

    return (
      <div
        key={studentId}
        onClick={() => !readOnly && handleToggle(studentId)}
        className={`relative p-4 sm:p-5 border-2 rounded-xl transition-all cursor-pointer hover:shadow-lg ${
          isPresent
            ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-400 hover:border-green-500 shadow-sm'
            : isAbsent
            ? 'bg-gradient-to-br from-red-50 to-rose-50 border-red-400 hover:border-red-500 shadow-sm'
            : 'bg-white border-gray-300 hover:border-blue-400 hover:bg-blue-50'
        } ${readOnly ? 'cursor-default' : ''}`}
      >
        <div className="absolute top-2 right-2">
          <input
            type="checkbox"
            checked={isPresent}
            onChange={() => handleToggle(studentId)}
            disabled={readOnly}
            onClick={(e) => e.stopPropagation()}
            className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
          />
        </div>
        <div className="text-center">
          {showBatchGroups && (
            <span className="inline-block mb-2 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
              {student.batch || 'Other students'}
            </span>
          )}
          {student.student_no && (
            <div className="mb-2 sm:mb-3">
              <span className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold text-blue-600 break-all">
                {student.student_no}
              </span>
            </div>
          )}
          <div className="flex items-center justify-center space-x-1 mb-2 sm:mb-3">
            <MdPerson className="text-gray-400 text-xs sm:text-sm flex-shrink-0" />
            <span className="font-medium text-gray-800 text-xs sm:text-sm truncate max-w-full">
              {extractStudentName(student.student_name)}
            </span>
          </div>
          <div className="mt-2">
            {isPresent && <span className="inline-block px-2 sm:px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Present</span>}
            {isAbsent && <span className="inline-block px-2 sm:px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Absent</span>}
            {!isPresent && !isAbsent && <span className="inline-block px-2 sm:px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">Not Marked</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search and Bulk Actions Section */}
      {showSearch && (
        <div className="space-y-4 pb-4 border-b border-gray-200">
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by name, student number, or batch..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            />
          </div>

          {!readOnly && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleSelectAll('present')}
                className="px-4 py-2 text-sm font-semibold bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors border border-green-300"
              >
                Mark All Present
              </button>
              <button
                onClick={() => handleSelectAll('absent')}
                className="px-4 py-2 text-sm font-semibold bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors border border-red-300"
              >
                Mark All Absent
              </button>
            </div>
          )}
        </div>
      )}

      {/* Summary Section */}
      <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-wrap gap-4 sm:gap-6 text-sm">
          <div>
            <span className="text-gray-600 font-medium">Total:</span>
            <span className="ml-2 font-bold text-gray-900">{totalCount}</span>
          </div>
          <div>
            <span className="text-green-600 font-medium">Present:</span>
            <span className="ml-2 font-bold text-green-700">{presentCount}</span>
          </div>
          <div>
            <span className="text-red-600 font-medium">Absent:</span>
            <span className="ml-2 font-bold text-red-700">{absentCount}</span>
          </div>
        </div>
        {searchTerm && (
          <span className="text-gray-600 text-xs sm:text-sm font-medium bg-white px-3 py-1 rounded-full border border-gray-300">
            Showing {filteredStudents.length} of {students.length} students
          </span>
        )}
      </div>

      {/* Student Cards Grid Section */}
      {filteredStudents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 font-medium text-base">
            {searchTerm ? 'No students found matching your search.' : 'No students in this session.'}
          </p>
        </div>
      ) : (
        showBatchGroups ? (
          <div className="space-y-8">
            {studentGroups.map(([batch, batchStudents]) => (
              <section key={batch} aria-label={`${batch} students`}>
                <div className="flex items-center gap-2 mb-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <MdSchool className="text-blue-600 text-xl" />
                  <h3 className="font-bold text-blue-900">{batch}</h3>
                  <span className="ml-auto text-sm font-semibold text-blue-800">
                    {batchStudents.length} {batchStudents.length === 1 ? 'student' : 'students'}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                  {batchStudents.map(renderStudentCard)}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {filteredStudents.map(renderStudentCard)}
          </div>
        )
      )}
    </div>
  );
}

export default StudentList;

